import store, { waitForUser } from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { ProductCardData } from '../../product-card/product-card-display';
import { WishList, WishLists } from '../interfaces/wish-list-items.interface';
import { WishListProductAction } from '../interfaces/wish-list-update-product.interface';
import { addProductToWishList } from '../thunks/wish-list-add-product.thunk';
import { clearUpdateProductOnWishListComment } from '../store/wish-list.store';
import { connect } from 'pwa-helpers';
import { css, html, PropertyValueMap, render, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { fetchAllWishLists } from '../thunks/wish-list-fetch-all-wish-lists.thunks';
import { getFilteredWishLists } from '../utils/wish-list-search.util';
import { getUpdateProductOnWishListComment, getWishListsData } from '../store/wish-list.selector';
import { removeProductFromWishList } from '../thunks/wish-list-remove-product.thunk';
import { renderErrorToast, renderUpdateProductSuccessToast } from '../utils/wish-list-toast.util';
import {
  getAddProductToWishListRequest,
  getRemoveProductFromWishListRequest,
  isProductInWishList,
} from '../utils/wish-list-product.util';
import {
  isAddProductToWishListSuccess,
  isRemoveProductFromWishListSuccess,
  isWishListsSuccess,
} from '../utils/wish-list.util';

@customElement('mhecomm-wish-list-update-product')
export class WishListUpdateProduct extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .wish-lists {
        min-height: 25vh;
        max-height: 35vh;
        overflow-x: hidden;
        overflow-y: auto;
        padding-right: 0.5rem;
        scrollbar-color: grey transparent;
      }

      .wish-lists::-webkit-scrollbar {
        width: 0.625rem;
      }

      .wish-lists::-webkit-scrollbar-thumb,
      .wish-lists::-webkit-scrollbar-track {
        background: transparent;
      }
    `,
  ];

  /**
   * If the component is used inside an off-canvas, this should be set to the off-canvas ID.
   */
  @property()
  offCanvasId = '';

  /**
   * The product to add to a wish list.
   */
  @property({ type: Object })
  product?: ProductCardData;

  /**
   * The placeholder on the search input.
   */
  @property()
  searchPlaceholder = 'Search for your lists...';

  /**
   * The filtered wish lists.
   */
  @state()
  private filteredWishlists: WishList[] = [];

  /**
   * The comment to add to the product on the wish list.
   */
  @state()
  private productComment = '';

  /**
   * The search keyword to filter the wish lists.
   */
  @state()
  private searchKeyword = '';

  /**
   * The customer wish lists.
   */
  @state()
  private wishLists?: WishLists;

  /**
   * A flag to determine if the wish lists were successfully loaded.
   */
  @state()
  private wishListsSuccess = false;

  /**
   * The ID of the wishlist being updated.
   */
  @state()
  private wishListsUpdateId?: number = undefined;

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(this, 'mhecomm-wish-list-add-product', this.onAddProduct);
    this.addTrackedEventListener(this, 'mhecomm-wish-list-remove-product', this.onRemoveProduct);

    waitForUser().then((): void => {
      store.dispatch(fetchAllWishLists());
    });
  }

  updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('searchKeyword')) {
      this.filteredWishlists = getFilteredWishLists(this.searchKeyword, this.wishLists);
    }
  }

  stateChanged(state: any): void {
    this.productComment = getUpdateProductOnWishListComment(state);
    this.wishListsSuccess = isWishListsSuccess();

    if (this.wishListsSuccess) {
      this.wishLists = getWishListsData(state);
      this.filteredWishlists = getFilteredWishLists(this.searchKeyword, this.wishLists);
    }
  }

  renderComp(): TemplateResult {
    // We removed the loader due to rendering issues.
    if (this.wishLists?.items && this.wishLists.items.length > 0) {
      return this.displayWishLists();
    }

    return this.renderNoWishListsTemplate();
  }

  /**
   * Displays the wish lists and a search input to filter them.
   */
  displayWishLists(): TemplateResult {
    return html`
      <div>
        <div>
          <div class="w-100">
            <input
              id="search-wish-list"
              type="text"
              aria-label="${this.searchPlaceholder}"
              class="form-control fs-small rounded ms-0"
              placeholder="${this.searchPlaceholder}"
              @input=${(e: any) => (this.searchKeyword = e.target.value)}
            />
          </div>
        </div>
        <div>
          <div class="my-2">
            <span class="fs-small fst-italic text-gray">Showing ${this.filteredWishlists.length} list(s).</span>
          </div>
          <div class="wish-lists">
            ${this.filteredWishlists?.map(
              (wishList: WishList) => html`
                <mhecomm-wish-list-list-display
                  .isProductInList=${isProductInWishList(this.product, wishList)}
                  .isProductUpdating=${this.wishListsUpdateId === wishList.wishlist_id}
                  .offCanvasId=${this.offCanvasId}
                  .product=${this.product}
                  .showActionButton="${true}"
                  .showUpdateButton="${true}"
                  .wishList=${wishList}
                  .wishListUpdateId=${this.wishListsUpdateId}
                >
                </mhecomm-wish-list-list-display>
              `
            )}
          </div>
          <div class="wish-lists-toast"></div>
        </div>
      </div>
    `;
  }

  /**
   * Renders if no data is present.
   */
  renderNoWishListsTemplate(): TemplateResult {
    return html`
      <div id="no-wish-lists" class="row gx-0">
        <div class="col col-3">
          <img
            src="/uxt/static/external/misc/no-wish-list.svg"
            class="w-15 h-15"
            alt="No-Wishlists"
            aria-label="No-Wishlists"
          />
        </div>
        <div class="col">
          <h4 aria-label="You don't have any lists yet.">You don't have any lists yet.</h4>
          <p aria-label="Start creating lists to save products you want to reference again later.">
            Start creating lists to save products you want to reference again later.
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Handles the event to add a product to a wish list.
   *
   * @param event
   */
  private onAddProduct(event: CustomEventInit): void {
    const product = event?.detail?.product;
    const wishList = event?.detail?.wishList;

    this.wishListsUpdateId = wishList.wishlist_id;

    store
      .dispatch(
        addProductToWishList(getAddProductToWishListRequest(this.productComment, this.product, wishList.wishlist_id))
      )
      .then((): void => this.handleApiResponse(WishListProductAction.ADD, product, wishList))
      .catch((): void => this.handleErrorResponse(WishListProductAction.ADD))
      .finally((): void => {
        this.wishListsUpdateId = undefined;
      });
  }

  /**
   * Handles the event to remove a product from a wish list.
   *
   * @param event
   */
  private onRemoveProduct(event: CustomEventInit): void {
    const product = event?.detail?.product;
    const wishList = event?.detail?.wishList;
    const request = getRemoveProductFromWishListRequest(wishList, event?.detail?.wishListItemId);

    this.wishListsUpdateId = wishList.wishlist_id;

    store
      .dispatch(removeProductFromWishList(request))
      .then((): void => this.handleApiResponse(WishListProductAction.REMOVE, product, wishList))
      .catch((): void => this.handleErrorResponse(WishListProductAction.REMOVE))
      .finally((): void => {
        this.wishListsUpdateId = undefined;
      });
  }

  /**
   * Handles the API response when updating a product on a wish list.
   *
   * @param action
   * @param product
   * @param wishList
   */
  private handleApiResponse(action: WishListProductAction, product?: ProductCardData, wishList?: WishList): void {
    if (isAddProductToWishListSuccess() || isRemoveProductFromWishListSuccess()) {
      return this.handleSuccessResponse(action, product, wishList);
    }

    this.handleErrorResponse(action);
  }

  /**
   * Handles success response when updating a product on a wish list.
   *
   * @param action
   * @param product
   * @param wishList
   */
  private handleSuccessResponse(action: WishListProductAction, product?: ProductCardData, wishList?: WishList): void {
    render(renderUpdateProductSuccessToast(action, product, wishList), this.getToast());
    waitForUser().then((): void => {
      store.dispatch(fetchAllWishLists());
    });

    store.dispatch(clearUpdateProductOnWishListComment());
  }

  /**
   * Handles error response when updating a product on a wish list.
   *
   * @param action
   */
  private handleErrorResponse(action: WishListProductAction): void {
    render(
      renderErrorToast({
        error:
          action === WishListProductAction.ADD
            ? ErrorToastWishList.ADDING_PRODUCT_TO_WISH_LIST
            : ErrorToastWishList.REMOVING_PRODUCT_FROM_WISH_LIST,
        displayLoadingTemplate: true,
        autoHide: false,
        hideClose: true,
      }),
      this.getToast()
    );
  }

  /**
   * Gets the toast element.
   */
  private getToast(): HTMLElement {
    return this.shadowRoot?.querySelector('.wish-lists-toast') as HTMLElement;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-update-product': WishListUpdateProduct;
  }
}
